/**
 * NotFoundPage (session 126t, fix #1) — explicit 404 instead of the
 * silent `<Navigate to="/" />` catch-all that hid real navigation
 * bugs and frustrated users with stale bookmarks.
 *
 * Three goals:
 *   1. Tell the user clearly that the page doesn't exist.
 *   2. Surface the offending path so they can see typos / spot
 *      broken in-app links during testing.
 *   3. Offer one tap back to a useful place (Home for signed-in
 *      users, login for anonymous).
 *
 * Telemetry hook: we POST the missed path to /api/log/404 (best-effort,
 * fire-and-forget) so support can see which broken links are getting
 * hit most often without needing to dig in browser session replay.
 */
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth';
import PikBrand from '../components/brand/PikBrand';

export default function NotFoundPage() {
  const location = useLocation();
  const { user } = useAuth();
  const isAuthed = Boolean(user);

  // Best-effort telemetry — never block render or surface failures.
  useEffect(() => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/log/404`;
      if (navigator.sendBeacon) {
        const data = new Blob(
          [JSON.stringify({ path: location.pathname + location.search })],
          { type: 'application/json' },
        );
        navigator.sendBeacon(url, data);
      }
    } catch {/* swallow — telemetry is best-effort */}
  }, [location.pathname, location.search]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-6"
      data-testid="not-found-page"
    >
      <div className="w-full max-w-md text-center space-y-6">
        <div className="text-7xl font-heading font-extrabold text-nav-blue/15 select-none leading-none">
          404
        </div>
        <div className="space-y-2">
          <h1
            className="font-heading text-2xl font-bold text-foreground"
            data-testid="not-found-title"
          >
            We couldn&rsquo;t find that page
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            The link you followed may be broken, or the page may have moved.
          </p>
          {location.pathname && location.pathname !== '/' && (
            <p
              className="text-xs text-muted-foreground/80 font-mono bg-muted/40 rounded-md px-2 py-1 inline-block mt-1"
              data-testid="not-found-attempted-path"
            >
              {location.pathname}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 pt-2">
          <Link to={isAuthed ? '/' : '/login'} className="block">
            <Button
              className="w-full h-11 rounded-full bg-nav-blue hover:bg-nav-blue/90 text-white"
              data-testid="not-found-home-btn"
            >
              <ArrowLeft size={16} className="mr-2" />
              {isAuthed ? 'Back to Home' : 'Back to Sign In'}
            </Button>
          </Link>
          {isAuthed && (
            <Link to="/search" className="block">
              <Button
                variant="outline"
                className="w-full h-11 rounded-full"
                data-testid="not-found-search-btn"
              >
                <Search size={16} className="mr-2" />
                Search your contacts instead
              </Button>
            </Link>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground/70 flex items-center justify-center gap-1 pt-4">
          <HelpCircle size={12} />
          Lost on <PikBrand />? Email <a href="mailto:support@peopleiknow.ai" className="underline">support@peopleiknow.ai</a>
        </p>
      </div>
    </div>
  );
}
